import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Field, Form, Input, Link, Radio, TextArea, Typography } from '@/components';
import { Route as RoutePath } from '@/constants';
import { DocumentUpload, FileSchema } from '@/features/files';
import { useUploadOffer } from '@/features/offers/hooks';
import { useRemoveAttachment } from '@/features/projects';
import { nullableStringSetter } from '@/helpers';
import { UseFormRegister } from 'react-hook-form';
import { AdvisoryServiceForm, AdvisoryServiceFormFields } from '@/features/services';
import { BuyerType } from '@/features/offers/types';

export type CreateOfferFormFields = {
  // General Details
  express_interest: string;
  eoi_file: string | null;
  price_range: string | null;

  // Buyer
  buyer_type: BuyerType | '';
  buyer_comments: string;
  //buyer_website: string;
  //buyer_presentation: string | null;

  // Others
  /* file: string | null;
  comments: string; */

  // Advisory service
  advisory_service_information: {
    legal_advisors: string;
    financial_advisors: string;
    technical_advisors: string;
    epc_service: string;
    //om_service: string;
    //other: string;
  };

  // Terms and conditions
  organization_approval: boolean;
  terms_and_conditions_accepted: boolean;

  submitted_eoi_file: string;
  is_submitted_eoi_file_generated: boolean;
};

export type CreateOfferFormProps = {
  readonly id: string;
  readonly onSubmit: (data: CreateOfferFormFields) => void;
  readonly corporateFile: FileSchema[];
  readonly onCorporateFileChange: Dispatch<SetStateAction<FileSchema[]>>;
  readonly file: FileSchema[];
  readonly onFileChange: Dispatch<SetStateAction<FileSchema[]>>;
  readonly eoiFile: FileSchema[];
  readonly onEoiFileChange: Dispatch<SetStateAction<FileSchema[]>>;
};

export function CreateOfferForm({
  id,
  onSubmit,
  file,
  onFileChange,
  corporateFile,
  onCorporateFileChange,
  eoiFile,
  onEoiFileChange,
}: CreateOfferFormProps) {
  const { t } = useTranslation(['offers']);

  const { uploadOfferFile } = useUploadOffer();
  const { deleteAttachment } = useRemoveAttachment();

  const textareaMaxLength = 4000;
  const textareaMinLength = 100;
  const inputMaxLength = 200;

  const options = {
    defaultValues: {
      // General Details
      express_interest: '',
      eoi_file: null,
      price_range: '',

      // Buyer
      buyer_type: '' as const,
      buyer_comments: '',
      //buyer_website: '',
      //buyer_presentation: null,

      // Others
      //file: null,
      //comments: undefined,

      // Advisory service
      advisory_service_information: {
        legal_advisors: '',
        financial_advisors: '',
        technical_advisors: '',
        epc_service: '',
        //om_service: '',
        //other: '',
      },

      // Terms and conditions
      organization_approval: false,
      terms_and_conditions_accepted: false,
    },
  };

  return (
    <Form<CreateOfferFormFields> id={id} onSubmit={onSubmit} options={options}>
      {({ formState, register, setValue, watch }) => (
        <div className="space-y-6">
          <Typography as="p" size="body-3" color="primary" weight="normal" className="mt-1 italic">
            {t('Please, submit a ')}
            <b>{t('non-binding Expression of Interest (EoI)')}</b>
            {t(' to explore this opportunity. Your EOI will be ')}
            <b>{t('shared confidentially and directly with the seller')}</b>
            {t(
              ' to assess alignment. If aligned, nTeaser will coordinate the next steps, including a kick-off meeting between both parties.'
            )}
          </Typography>
          <div>
            <Typography as="h4" color="primary" size="title" weight="semibold">
              {t('Transaction approach')}
            </Typography>
            <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
              {t(
                'Describe how you would approach this opportunity, including your interest in the project, the transaction structure you are considering, your expected timing and, if available, an indicative price or price range. All information is non-binding and shared confidentially with the seller.'
              )}
            </Typography>
          </div>
          <Field
            id="express_interest"
            label={t('Expression of Interest')}
            error={formState.errors['express_interest']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="express_interest"
              color={formState.errors['express_interest'] ? 'error' : 'primary'}
              registration={register('express_interest', {
                required: t('This field is required'),
                setValueAs: nullableStringSetter,
                maxLength: textareaMaxLength,
                minLength: {
                  value: textareaMinLength,
                  message: t('Min length of {{min}} characters', { min: textareaMinLength }),
                },
              })}
              maxLength={textareaMaxLength}
              minLength={textareaMinLength}
            />
          </Field>

          <Field
            id="eoi_file"
            label={t('Additional Documents: EOI, Corporate presentation, LOI, etc (optional) ')}
            delay={0.8}
          >
            <DocumentUpload
              multiple={false}
              files={eoiFile}
              onSelectFiles={(newFiles) => {
                uploadOfferFile(newFiles[0], (data) => {
                  setValue('eoi_file', data.id);
                  onEoiFileChange([data]);
                });
              }}
              onUnSelectFiles={(oldFile) => {
                deleteAttachment(oldFile.id, () => {
                  setValue('eoi_file', null);
                  onEoiFileChange([]);
                });
              }}
            />
          </Field>

          <Field
            id="price_range"
            label={t('Indicative price range (optional)')}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="price_range"
              color={formState.errors['price_range'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('price_range', {
                setValueAs: nullableStringSetter,
                maxLength: inputMaxLength,
              })}
              maxLength={inputMaxLength}
            />
          </Field>

          <hr />
          <div>
            <Typography as="h4" color="primary" size="title" weight="semibold">
              {t('Prospective Buyer Information')}
            </Typography>
            <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
              {t(
                'Provide information about the buyer behind this Expression of Interest, indicating whether you are acting as the final buyer or as a representative on behalf of a third party. If acting on behalf of a third party, please include their name and relevant details.'
              )}
            </Typography>
          </div>

          <Field
            id="buyer_type"
            label={t('Your Role in Transaction')}
            delay={0.9}
            error={formState.errors['buyer_type']}
            classes={{ field: 'space-y-2', input: 'space-y-3' }}
          >
            <Radio
              color={formState.errors['buyer_type'] ? 'error' : 'primary'}
              registration={register('buyer_type', { required: t('This field is required') })}
              value="final-buyer"
              classes={{ main: 'items-start', label: 'flex-1 min-w-0' }}
            >
              {' '}
              <Typography as="p" size="body-2" color="primary" weight="normal">
                {t('Final Buyer: You belong to the entity that will acquire and own the project.')}
              </Typography>
            </Radio>
            <Radio
              color={formState.errors['buyer_type'] ? 'error' : 'primary'}
              registration={register('buyer_type', { required: t(' ') })}
              value="intermediary"
              classes={{ main: 'items-start', label: 'flex-1 min-w-0' }}
            >
              <Typography as="p" size="body-2" color="primary" weight="normal">
                {t('Third-party representative: You are acting on behalf of another buyer or entity. ')}
              </Typography>
            </Radio>
            <Radio
              color={formState.errors['buyer_type'] ? 'error' : 'primary'}
              registration={register('buyer_type', { required: t('This field is required') })}
              value="debt-provider"
              classes={{ main: 'items-start', label: 'flex-1 min-w-0' }}
            >
              {' '}
              <Typography as="p" size="body-2" color="primary" weight="normal">
                {t('Debt provider: Your company can provide debt for this project.')}
              </Typography>
            </Radio>
          </Field>

          <Field
            id="buyer_comments"
            label={t('Final Buyer Description ')}
            delay={1.05}
            error={formState.errors['buyer_comments']}
          >
            <TextArea
              id="buyer_comments"
              color={formState.errors['buyer_comments'] ? 'error' : 'primary'}
              registration={register('buyer_comments', {
                required: t('This field is required'),
                setValueAs: nullableStringSetter,
                maxLength: textareaMaxLength,
                minLength: {
                  value: textareaMinLength,
                  message: t('Min length of {{min}} characters', { min: textareaMinLength }),
                },
              })}
              maxLength={textareaMaxLength}
              minLength={textareaMinLength}
            />
          </Field>

          {/* <Field
            id="buyer_website"
            label={t('Corporate website (optional)')}
            error={formState.errors['buyer_website']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="buyer_website"
              color={formState.errors['buyer_website'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('buyer_website', {
                setValueAs: nullableStringSetter,
                validate: (value) =>
                  !value ||
                  /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/.test(value) ||
                  t('Enter a valid URL'),
              })}
            />
          </Field> */}

          {/* <Field id="buyer_presentation" label={t('Corporate Presentation (optional)')} delay={1.35}>
            <DocumentUpload
              multiple={false}
              files={corporateFile}
              onSelectFiles={(newFiles) => {
                uploadOfferFile(newFiles[0], (data) => {
                  setValue('buyer_presentation', data.id);
                  onCorporateFileChange([data]);
                });
              }}
              onUnSelectFiles={(oldFile) => {
                deleteAttachment(oldFile.id, () => {
                  setValue('buyer_presentation', null);
                  onCorporateFileChange([]);
                });
              }}
            />
          </Field>
 */}
          <hr />
          {/* <div>
            <Typography as="h4" color="primary" size="title" weight="semibold">
              {t('Additional Information & Documents')}
            </Typography>
            <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
              {t(
                'Use this section to provide any additional information you consider relevant of your expression of interest or the project. You can also upload any supporting documents that may help clarify your position, terms, or requirements.'
              )}
            </Typography>
          </div>

          <Field id="comments" label={t('Additional Information (optional)')} delay={1.2}>
            <TextArea
              id="comments"
              color="primary"
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field id="document" label={t('Other relevant documents (optional)')} delay={1.35}>
            <DocumentUpload
              multiple={false}
              files={file}
              onSelectFiles={(newFiles) => {
                uploadOfferFile(newFiles[0], (data) => {
                  setValue('file', data.id);
                  onFileChange([data]);
                });
              }}
              onUnSelectFiles={(oldFile) => {
                deleteAttachment(oldFile.id, () => {
                  setValue('file', null);
                  onFileChange([]);
                });
              }}
            />
          </Field> 

          <hr />*/}
          <div>
            <Typography as="h4" color="primary" size="title" weight="semibold">
              {t('Advisory Services')}
            </Typography>
            <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
              {t(
                'nTeaser collaborates with a selected network of advisors to support buyers and sellers throughout the transaction process. Your selection is non-binding and allows us to identify potential areas where expert support may be helpful. Visit our'
              )}{' '}
              <Link to={RoutePath.Services} target="_blank">
                {t('Advisors section')}
              </Link>
              {'!'}
            </Typography>
          </div>

          <AdvisoryServiceForm
            register={register as UseFormRegister<AdvisoryServiceFormFields>}
            formState={formState}
          />

          <div className="space-y-4 rounded-md bg-gray-100 p-4">
            <Field
              id="organization_approval"
              error={formState.errors['organization_approval']}
              delay={0.9}
              classes={{ field: 'space-y-1' }}
            >
              <Checkbox
                id="organization_approval"
                color="primary"
                classes={{ main: 'items-start', label: 'flex-1 min-w-0' }}
                registration={register('organization_approval', {
                  required: t('This consent is required'),
                })}
              >
                <Typography as="p" color="secondary" size="body-2" weight="normal">
                  {t(
                    'I confirm that I have received the necessary internal approvals to submit this Expression of Interest on behalf of my organization.'
                  )}
                </Typography>
              </Checkbox>
            </Field>
            <Field
              id="terms_and_conditions_accepted"
              error={formState.errors['terms_and_conditions_accepted']}
              delay={1.05}
              classes={{ field: 'space-y-1' }}
            >
              <Checkbox
                id="terms_and_conditions_accepted"
                color="primary"
                classes={{ main: 'items-start', label: 'flex-1 min-w-0' }}
                registration={register('terms_and_conditions_accepted', {
                  required: t('You must accept the terms and conditions to submit this offer.'),
                })}
              >
                <Typography as="p" color="secondary" size="body-2" weight="normal">
                  {t("I confirm that I have read and accepted nTeaser's ")}
                  <Link to={RoutePath.TermsAndConditions} target="_blank">
                    {t('Terms and Conditions')}
                  </Link>
                  {t(', including the 1% success fee payable upon signing a SPA.')}
                </Typography>
                <Typography as="span" color="secondary" size="body-3" className="italic">
                  {t('A copy of the document will be downloaded to your computer when you submit the form.')}
                </Typography>
              </Checkbox>
            </Field>
          </div>
        </div>
      )}
    </Form>
  );
}
